/**
 * useWebchatWebSocket — visitor-scoped WebSocket connection.
 *
 * Connects to the shared WebSocket API using the visitor JWT so operator
 * messages show up in real time. Falls back silently if VITE_WS_URL is not
 * configured or the browser cannot connect (REST polling is an option the
 * widget can enable later if needed).
 *
 * Heartbeat: the visitor's tab can stay open for long stretches without
 * activity. A client-side ping every 4 min keeps the API Gateway from closing
 * the socket (default idle timeout: 10 min). If pongs stop arriving, we force
 * a reconnect rather than wait for TCP to notice a half-open socket.
 */

import { useEffect, useRef, useState } from 'react';
import { WebchatMessageItem } from '@/services/webchat.service';

const WS_URL = import.meta.env.VITE_WS_URL as string | undefined;
const RECONNECT_DELAY_BASE = 1000;
const RECONNECT_DELAY_MAX = 30000;
const PING_INTERVAL_MS = 4 * 60 * 1000;
const PONG_TIMEOUT_MS = 9 * 60 * 1000;

interface WsNewMessagePayload {
  conversationId: string;
  message: {
    id: string;
    direction: 'inbound' | 'outbound';
    senderName?: string;
    content?: { body?: string };
    createdAt: string;
  };
}

interface WsEvent {
  type: string;
  data: unknown;
}

export interface UseWebchatWebSocketOptions {
  token: string | null;
  conversationId: string | null;
  onMessage: (message: WebchatMessageItem) => void;
}

export function useWebchatWebSocket({
  token,
  conversationId,
  onMessage,
}: UseWebchatWebSocketOptions): { connected: boolean } {
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const pingIntervalRef = useRef<number | null>(null);
  const lastPongAtRef = useRef<number>(Date.now());
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    if (!WS_URL || !token || !conversationId) return;

    let cancelled = false;

    const clearPing = () => {
      if (pingIntervalRef.current != null) {
        window.clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };

    const connect = () => {
      if (cancelled) return;
      const ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;

      ws.onopen = () => {
        reconnectAttemptRef.current = 0;
        lastPongAtRef.current = Date.now();
        setConnected(true);

        clearPing();
        pingIntervalRef.current = window.setInterval(() => {
          if (ws.readyState !== WebSocket.OPEN) return;
          try {
            ws.send(JSON.stringify({ action: 'ping' }));
          } catch {
            // Fall through to onerror/onclose handling.
          }
          if (Date.now() - lastPongAtRef.current > PONG_TIMEOUT_MS) {
            try {
              ws.close();
            } catch {
              // onclose will trigger reconnect.
            }
          }
        }, PING_INTERVAL_MS);
      };

      ws.onclose = () => {
        setConnected(false);
        clearPing();
        if (cancelled) return;
        const attempt = ++reconnectAttemptRef.current;
        const delay = Math.min(
          RECONNECT_DELAY_BASE * 2 ** (attempt - 1),
          RECONNECT_DELAY_MAX
        );
        reconnectTimeoutRef.current = window.setTimeout(connect, delay);
      };

      ws.onerror = () => {
        ws.close();
      };

      ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data) as WsEvent;
          if (parsed.type === 'pong') {
            lastPongAtRef.current = Date.now();
            return;
          }
          if (parsed.type !== 'new_message') return;
          const data = parsed.data as WsNewMessagePayload;
          if (!data?.message) return;
          if (data.conversationId !== conversationId) return;
          onMessageRef.current({
            id: data.message.id,
            direction: data.message.direction,
            senderName: data.message.senderName,
            text: data.message.content?.body,
            createdAt: data.message.createdAt,
          });
        } catch {
          // Ignore malformed events.
        }
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimeoutRef.current != null) {
        window.clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      clearPing();
      wsRef.current?.close();
      wsRef.current = null;
      setConnected(false);
    };
  }, [token, conversationId]);

  return { connected };
}
